import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { FeUtilityService } from '../fe-utility.service';
import { AuthorizationService } from './authorization.service';

@Injectable({
  providedIn: 'root',
})
export class TabManagerService {
  private tabId = `h-${Math.random()}`.slice(2, 11);
  private readonly TAB_LIMIT = 5;
  private activeTabs: Set<string> = new Set();
  private broadcastChannel: BroadcastChannel;

  constructor(
    private router: Router,
    private ngZone: NgZone,
    private feUtil: FeUtilityService,
    private authorizationService: AuthorizationService
  ) {
    this.broadcastChannel = new BroadcastChannel('tab_management');
    this.initializeBroadcastChannel();
    this.registerTab();
    window.addEventListener('beforeunload', this.cleanupOnClose.bind(this));
  }

  private initializeBroadcastChannel(): void {
    this.broadcastChannel.onmessage = ({ data }) => {
      const { type, tabId, application_name, application_id, sourceTabId } = data;
      switch (type) {
        case 'tab_opened':
          this.activeTabs.add(tabId);
          break;
        case 'tab_closed':
          this.activeTabs.delete(tabId);
          break;
        case 'request_active_tabs':
          this.broadcastChannel.postMessage({
            type: 'tab_opened',
            tabId: this.tabId,
          });
          break;
        case 'application_changed':
          if (sourceTabId === this.tabId) break;
          this.ngZone.run(() => this.syncApplicationAndNavigate(application_name, application_id));
          break;
        case 'logout':
          this.ngZone.run(() => {
            this.authorizationService.clearStoreState();
            const loginUrl = `${window.location.origin}${window.location.pathname || ''}#/auth/login?tabReload=true`;
            window.location.assign(loginUrl);
          });
          break;
      }
    };
    this.requestActiveTabs();
  }

  private syncApplicationAndNavigate(applicationName: string, applicationId?: number): void {
    if (!applicationName) return;
    const appNameLower = String(applicationName).toLowerCase();

    const id = applicationId ?? this.feUtil.applications?.find(
      (a: { id: number; name?: string }) => (a.name || '').toLowerCase() === appNameLower
    )?.id;

    if (id != null) {
      this.feUtil.switchApplication(id);
    }

    const hash = `#/dashboard/${appNameLower}`;
    if (window.location.hash !== hash) {
      window.location.hash = hash;
    }
  }

  private requestActiveTabs(): void {
    setTimeout(() => {
      this.broadcastChannel.postMessage({
        type: 'request_active_tabs',
        tabId: this.tabId,
      });
    }, 100);
  }

  private registerTab(): void {
    this.activeTabs.add(this.tabId);
    this.broadcastChannel.postMessage({
      type: 'tab_opened',
      tabId: this.tabId,
    });
    setTimeout(() => {
      if (this.isTabLimitExceeded()) {
        this.router.navigate(['/max-tabs-limit-exceeded']);
      }
    }, 200);
  }

  public isTabLimitExceeded(): boolean {
    return this.activeTabs.size > this.TAB_LIMIT;
  }

  public getTabId(): string {
    return this.tabId;
  }
  
  public cleanupOnClose(): void {
    this.activeTabs.delete(this.tabId);
    this.broadcastChannel.postMessage({
      type: 'tab_closed',
      tabId: this.tabId,
    });
    this.broadcastChannel.close();
  }
}
